import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import teamAPI from "../../../services/teamApi";
import { TEAM_BASE, TEAM_ROLE_LABELS } from "./teamConstants";
import TeamMemberManagePanel from "./TeamMemberManagePanel";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { isOwner as checkIsOwner } from "../../../utils/roles";
import { HubScreen, HubSegment } from "../AdminPortal/HubPwa";
import { useHubBasePath } from "../../../utils/hubPaths";
import { DOCTORS_BASE } from "../AdminDoctors/doctorConstants";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import {
  Avatar,
  Card,
  EmptyState,
  SkeletonCard,
  Tabs,
  useToast,
} from "../../ui";

const CATEGORY_LABELS = {
  access: "Доступ",
  clients: "Клиенты",
  schedule: "Расписание",
  settings: "Настройки",
  other: "Прочее",
};

function formatAuditWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay) return `Сегодня, ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return `Вчера, ${time}`;
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function memberAccessStatus(member) {
  if (!member) return "";
  if (member.trainer_active === false || member.is_blocked) {
    return "Деактивирован";
  }
  return "Активен";
}

export default function AdminTeamMember() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isApp = useAdminHubUi();
  const hubBase = useHubBasePath();
  const profile = useSelector((state) => state.auth.profile);
  const currentUserId = profile?.id;
  const viewerIsOwner = checkIsOwner(profile?.role);

  const [tab, setTab] = useState("access");
  const [member, setMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await teamAPI.list();
      const list = res.members || [];
      setMembers(list);
      const found = list.find((m) => String(m.id) === String(userId));
      if (!found) {
        setMember(null);
        return;
      }
      setMember(found);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить");
      setMember(null);
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  const loadAudit = useCallback(async () => {
    if (!userId) return;
    setAuditLoading(true);
    try {
      const res = await teamAPI.getMemberAudit(userId);
      setAudit(res.audit || []);
    } catch {
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const canViewJournal = !!member && (!member.is_owner || viewerIsOwner);

  useEffect(() => {
    if (!member) return;
    if (!canViewJournal) {
      setAudit([]);
      setAuditLoading(false);
      return;
    }
    loadAudit();
  }, [member, canViewJournal, loadAudit]);

  useEffect(() => {
    if (!canViewJournal && tab === "journal") {
      setTab("access");
    }
  }, [canViewJournal, tab]);

  const canManage =
    member &&
    member.id !== currentUserId &&
    !member.is_owner &&
    (viewerIsOwner || member.role === "trainer");

  const canMessage = member && member.id !== currentUserId;

  const openMessage = () => {
    if (!member?.id) return;
    navigate(`${hubBase}/chat/member/${member.id}`, {
      state: { name: member.full_name || undefined },
    });
  };

  const tabItems = useMemo(() => {
    const items = [{ id: "access", label: "Доступ" }];
    if (canViewJournal) {
      items.push({
        id: "journal",
        label: audit.length ? `Журнал · ${audit.length}` : "Журнал",
      });
    }
    return items;
  }, [canViewJournal, audit.length]);

  const accessTab = member ? (
    <div className={aa.teamMemberTabBody}>
      {member.phone || member.email ? (
        <div className={aa.teamAccessContacts}>
          {member.email ? <span>{member.email}</span> : null}
          {member.phone ? <span>{member.phone}</span> : null}
        </div>
      ) : null}

      {canManage ? (
        <TeamMemberManagePanel
          member={member}
          members={members}
          viewerIsOwner={viewerIsOwner}
          onChanged={(next) => {
            if (!next) {
              navigate(TEAM_BASE);
              return;
            }
            load();
            loadAudit();
          }}
        />
      ) : member.id === currentUserId ? (
        <p className={aa.hubHint}>
          Это ваш профиль. Сменить пароль можно в настройках.
        </p>
      ) : (
        <p className={aa.hubHint}>Недостаточно прав для управления доступом.</p>
      )}

      {member.trainer_id && member.role === "trainer" ? (
        <button
          type="button"
          className={aa.teamAccessLinkBtn}
          onClick={() => navigate(`${DOCTORS_BASE}/${member.trainer_id}`)}
        >
          Профиль тренера
        </button>
      ) : null}
    </div>
  ) : null;

  const journalTab = (
    <div className={aa.teamMemberTabBody}>
      {auditLoading ? (
        <SkeletonCard />
      ) : audit.length === 0 ? (
        <EmptyState
          title="Журнал пуст"
          description="Здесь появятся действия сотрудника: клиенты, группы, приглашения, смена пароля и другие операции."
        />
      ) : (
        <ol className={aa.teamAuditList}>
          {audit.map((log) => (
            <li key={log.id} className={aa.teamAuditItem}>
              <div className={aa.teamAuditDot} aria-hidden />
              <div className={aa.teamAuditContent}>
                <div className={aa.teamAuditTop}>
                  <span className={aa.teamAuditLabel}>
                    {log.label || "Действие в системе"}
                  </span>
                  <time className={aa.teamAuditWhen} dateTime={log.createdAt}>
                    {formatAuditWhen(log.createdAt)}
                  </time>
                </div>
                {log.detail ? (
                  <div className={aa.teamAuditDetail}>{log.detail}</div>
                ) : null}
                <div className={aa.teamAuditCat}>
                  {CATEGORY_LABELS[log.category] || CATEGORY_LABELS.other}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );

  const tabContent =
    tab === "journal" && canViewJournal ? journalTab : accessTab;

  if (loading && !member) {
    return (
      <div className={isApp ? undefined : s.patientPage}>
        {isApp ? (
          <HubScreen title="Сотрудник" onBack={() => navigate(TEAM_BASE)}>
            <SkeletonCard />
          </HubScreen>
        ) : (
          <p className={s.timelineLoading}>Загрузка…</p>
        )}
      </div>
    );
  }

  if (!member) {
    const empty = (
      <EmptyState
        title="Сотрудник не найден"
        actionLabel="К команде"
        onAction={() => navigate(TEAM_BASE)}
      />
    );
    if (isApp) {
      return (
        <HubScreen title="Сотрудник" onBack={() => navigate(TEAM_BASE)}>
          {empty}
        </HubScreen>
      );
    }
    return <div className={s.patientPage}>{empty}</div>;
  }

  const profileHeader = (
    <header className={aa.teamMemberHead}>
      <div className={aa.teamMemberHeadMain}>
        <Avatar name={member.full_name || "?"} size="lg" />
        <div className={aa.teamMemberProfileInfo}>
          <h1 className={aa.teamMemberProfileName}>
            {member.full_name || "Сотрудник"}
          </h1>
          <p className={aa.teamMemberHeadMeta}>
            {TEAM_ROLE_LABELS[member.role] || member.role}
            <span aria-hidden>·</span>
            {memberAccessStatus(member)}
          </p>
        </div>
      </div>
      {canMessage ? (
        <button
          type="button"
          className={aa.teamMemberChatBtn}
          onClick={openMessage}
        >
          Написать
        </button>
      ) : null}
    </header>
  );

  if (isApp) {
    return (
      <HubScreen
        title={member.full_name || "Сотрудник"}
        onBack={() => navigate(TEAM_BASE)}
        bodyClassName={aa.teamMemberAppBody}
      >
        {profileHeader}
        {tabItems.length > 1 ? (
          <HubSegment tabs={tabItems} active={tab} onChange={setTab} />
        ) : null}
        <section className={aa.teamMemberSection}>{tabContent}</section>
      </HubScreen>
    );
  }

  return (
    <div className={s.patientPage}>
      <nav className={`${s.patientBreadcrumbs} ${s.patientPageBlock}`}>
        <Link to={TEAM_BASE}>Команда</Link>
        <span aria-hidden>/</span>
        <span>{member.full_name || "Сотрудник"}</span>
      </nav>

      {profileHeader}

      <Card className={s.patientPageBlock}>
        {tabItems.length > 1 ? (
          <Tabs items={tabItems} active={tab} onChange={setTab} />
        ) : null}
        {tabContent}
      </Card>
    </div>
  );
}
